import {
  onMounted,
  onUnmounted,
  ref,
  watch,
  type Ref,
  type WatchSource,
} from 'vue';

export type VisibleViewportRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const LAYOUT_VIEWPORT_OVERLAY_STYLE: Record<string, string> = {
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
};

export const computeIsTouchDevice = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

export const readVisibleViewportRect = (): VisibleViewportRect => {
  const visualViewport = window.visualViewport;
  if (!visualViewport) {
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  return {
    top: visualViewport.offsetTop,
    left: visualViewport.offsetLeft,
    width: visualViewport.width,
    height: visualViewport.height,
  };
};

export const getAppHeightPx = (): number => {
  if (computeIsTouchDevice()) {
    return readVisibleViewportRect().height;
  }

  return window.innerHeight;
};

export const syncAppHeightCssVariable = (): void => {
  document.documentElement.style.setProperty(
    '--app-height',
    `${String(getAppHeightPx())}px`,
  );
};

export const getFloatingOverlayHostStyle = (
  followVisualViewport = true,
): Record<string, string> => {
  if (!followVisualViewport || !computeIsTouchDevice()) {
    return { ...LAYOUT_VIEWPORT_OVERLAY_STYLE };
  }

  const rect = readVisibleViewportRect();
  return {
    top: `${String(rect.top)}px`,
    left: `${String(rect.left)}px`,
    width: `${String(rect.width)}px`,
    height: `${String(rect.height)}px`,
  };
};

export const useVisibleViewportSync = (onViewportChange?: () => void): void => {
  const notify = (): void => {
    syncAppHeightCssVariable();
    onViewportChange?.();
  };

  onMounted(() => {
    notify();
    window.addEventListener('resize', notify);
    window.visualViewport?.addEventListener('resize', notify);
    window.visualViewport?.addEventListener('scroll', notify);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', notify);
    window.visualViewport?.removeEventListener('resize', notify);
    window.visualViewport?.removeEventListener('scroll', notify);
  });
};

export const useFloatingOverlayHostStyle = (
  followVisualViewport?: WatchSource<boolean>,
): Ref<Record<string, string>> => {
  const shouldFollowVisualViewport = (): boolean => {
    if (!followVisualViewport) {
      return true;
    }

    return typeof followVisualViewport === 'function'
      ? followVisualViewport()
      : followVisualViewport.value;
  };

  const hostStyle = ref(
    getFloatingOverlayHostStyle(shouldFollowVisualViewport()),
  );

  const syncHostStyle = (): void => {
    hostStyle.value = getFloatingOverlayHostStyle(shouldFollowVisualViewport());
  };

  if (followVisualViewport) {
    watch(followVisualViewport, syncHostStyle);
  }

  useVisibleViewportSync(syncHostStyle);

  return hostStyle;
};
